import { useAuth } from "@/contexts/keycloak";
import { useAppSession } from "@/contexts/appSession";
import { useMemo, useRef } from "react";

/**
 * User object returned by useUser hook.
 * Combines Keycloak auth data with backend session data.
 */
export type User = {
  /** User's ID (from backend User table) */
  id: string;
  /** User's profile ID (from backend UserProfile table) */
  profileId: string | undefined;
  /** User's email address */
  email: string;
  /** User's display name (from Keycloak — typically the full name) */
  name: string;
  /** User's profile name (from UserProfile.name — the canonical username shown in chat) */
  profileName: string | undefined;
  /** User's first name */
  firstName: string | undefined;
  /** User's last name */
  lastName: string | undefined;
  /** User's avatar image URL */
  image: string | undefined;
  /** Current auth token */
  token: string | undefined;
  /** UI capabilities enabled for this user */
  uiCapabilities: string[];
};

/**
 * User hook that combines Keycloak auth with backend session data.
 *
 * Returns a tuple: [user, status, hasPermission, userReady]
 * - user: Complete user object with id, email, name, profileId, image, uiCapabilities, etc. (or undefined if not authenticated)
 * - status: "loading" | "authenticated" | "unauthenticated"
 * - hasPermission: Function to check if user has a specific permission
 * - userReady: Latch that becomes true once user was available (never resets to false)
 */
export const useUser = () => {
  const { user: keycloakUser, token, status, isAuthenticated } = useAuth();
  const { user: sessionUser, hasPermission: hasPermissionFromContext, isInitialized } = useAppSession();

  // Build a user object combining Keycloak auth + backend session data
  const user = useMemo(
    (): User | undefined => {
      if (!isAuthenticated || !keycloakUser) {
        return undefined;
      }

      // User ID comes from backend session (not Keycloak)
      // Only return complete user when session is initialized
      if (!sessionUser.userId) {
        return undefined;
      }

      return {
        id: sessionUser.userId,
        profileId: sessionUser.profileId,
        email: keycloakUser.email,
        name: keycloakUser.name,
        profileName: sessionUser.name,
        firstName: keycloakUser.firstName,
        lastName: keycloakUser.lastName,
        image: sessionUser.image,
        token: token,
        uiCapabilities: sessionUser.uiCapabilities,
      };
    },
    [isAuthenticated, keycloakUser, token, sessionUser, isInitialized]
  );

  /**
   * Check if user has a specific permission.
   * Delegates to AppSessionContext which handles wildcard and entity-level checks.
   * 
   * @param key - Permission key to check
   * @param workspaceId - Optional workspace ID for workspace-level permission check
   * @param orgId - Optional org ID for org-level permission check
   */
  const hasPermission = (key: string, workspaceId?: string, orgId?: string) => {
    if (status === "loading") {
      return false;
    }

    if (!user) {
      return false;
    }

    return hasPermissionFromContext(key, workspaceId, orgId);
  };

  // Latch: once user is available, remember it forever (survives disconnects)
  const userEverLoaded = useRef(false);
  if (user) {
    userEverLoaded.current = true;
  }

  return [user, status, hasPermission, userEverLoaded.current] as const;
};
